/**
 * useStageContextMenu — Track 3 Phase 7
 *
 * Owns the right-click stage context menu (AutoCAD SHORTCUTMENU
 * parity) extracted from `App.tsx` per
 * `docs/plans/2026-05-08_track3_app_decomposition.md` row 7
 * ("Cleanup: ribbon / context menu / inspector / coord-system").
 *
 * Responsibilities:
 *   - Track the transient open-state (`null` = closed; otherwise a
 *     `{ screenPos, mode }` snapshot used by `<StageContextMenu>`).
 *   - Compute the popup mode per AutoCAD SHORTCUTMENU docs:
 *       active draft session → "command" (bit 8)
 *       selection not empty  → "edit"    (bit 2)
 *       otherwise            → "default" (bit 1)
 *     If the bit for the matching mode is OFF in `SHORTCUTMENU_BITS`,
 *     right-click falls back to RIGHTCLICKENTER (legacy) by submitting
 *     an empty command — the draft state machine treats that as
 *     "finish current segment / confirm".
 *   - Build the per-mode menu item list (default 11 = 1+2+8) wired to
 *     existing reducer commands. Items mirror the AutoCAD reference
 *     list per Autodesk SHORTCUTMENU documentation (2023 release).
 *
 * Returned shape:
 *   { stageContextMenu, handleStageContextMenu, closeStageContextMenu,
 *     buildContextMenuItems }
 *
 * AutoCAD parity:
 *   The popup matches the AutoCAD shortcut-menu structure: every item
 *   that would require subsystems not yet implemented (Action Recorder,
 *   SteeringWheels, Subobject Filter, Find, QuickCalc) is surfaced as
 *   `disabled: true` so the menu structure matches AutoCAD without
 *   false-positive clicks.
 */

import { useCallback, useState } from "react";

import type {
  ContextMenuItem,
  ContextMenuMode,
} from "../components/StageContextMenu";
import type {
  EditorShellState,
  EditorCommand,
  DynamicBlockCommand,
} from "@dxf-ai-editor/contracts";
import type { DraftSession } from "../scene/draftTypes";
// W2.5 OSNAP-OVERRIDE-MODES-S2 — single source of truth for "does this
// DraftStep accept a point pick" (local Codex round-2 P2 fix).
import { getDraftStepDescriptor } from "../scene/drafting";
// W2.5 OSNAP-OVERRIDE-MODES-S2 — the Shift+right-click Object Snap menu arms
// `extint`/`extappint` directly via the sysvar store (same direct-store-
// access pattern already used elsewhere in this file's sibling modules for
// zustand stores, e.g. `useSelectionCyclingStore.getState()` in
// `stageInteractions.ts`).
import { useSysvarStore } from "../dynamicInput/sysvarStore";

/**
 * Translation function shape returned by `useT()` — duplicated locally
 * because `apps/web/src/app/i18n` does not re-export the type and we
 * need to type the hook params without coupling the hook to React's
 * generic `(key: string, vars?: object) => string`.
 */
type TFn = (
  key: Parameters<(typeof import("../i18n"))["t"]>[0],
  vars?: Record<string, string | number>,
) => string;

// AutoCAD SHORTCUTMENU default — bits 1+2+8 = 11. Default + Edit +
// Command-with-options. Hard-coded for now; a future PR can wire
// SHORTCUTMENU as a real system variable when the sysvar table grows
// that key.
const SHORTCUTMENU_BITS = 11;

export interface StageContextMenuState {
  screenPos: { x: number; y: number };
  mode: ContextMenuMode;
}

/**
 * cmd-pan r6 P2-1 — the id of the ONE menu item that must NOT supersede a live
 * realtime-PAN session, because selecting it STARTS a pan session (cancelling
 * itself would make the menu's 'Pan' item a no-op). Every OTHER item — current
 * and future, direct-`dispatch` or command-line — tears the session down via
 * the menu-level choke point in `buildContextMenuItems`.
 */
const PAN_ITEM_ID = "pan";

export interface UseStageContextMenuParams {
  /** Editor shell state (for selection + command-line transcript). */
  state: EditorShellState;
  /** Active draft session (truthy → "command" mode). */
  draftSession: DraftSession | null;
  /** i18n translator from `useT()`. */
  t: TFn;
  /** Used by menu items that submit text into the command line. */
  submitCommandTextRef: React.MutableRefObject<
    (rawText: string, source?: "user" | "batch") => boolean
  >;
  /** Editor-store dispatcher (for `shell/clear-selection` etc.). */
  dispatch: (command: EditorCommand | DynamicBlockCommand) => void;
  /**
   * cmd-pan r6 P2-1 — tear down an active realtime-PAN session (no-op when none
   * is active). Wired to App's `supersedePanSession`. The menu wraps EVERY item
   * action (except 'Pan') so a direct-`dispatch` item (`deselect-all`,
   * `properties`) that bypasses `submitCommandText`/`withPanSupersede` still
   * supersedes the pan — at the menu level, by construction, not per-item.
   */
  supersedePanSession: () => void;
  /**
   * W2.5 OSNAP-OVERRIDE-MODES-S2 — open the DSETTINGS dialog (Object Snap
   * tab) from the menu's "Osnap Settings..." item. Optional: when not
   * provided the item is `disabled` rather than crashing. Wired by App.tsx
   * to the same `setDsettingsOpen(true)` call `DynInputOverlay`'s
   * `onOpenSettings` already uses.
   */
  onOpenOsnapSettings?: () => void;
}

export interface UseStageContextMenuResult {
  stageContextMenu: StageContextMenuState | null;
  handleStageContextMenu: (
    screen: { x: number; y: number },
    shiftKey: boolean,
  ) => void;
  closeStageContextMenu: () => void;
  buildContextMenuItems: (
    mode: ContextMenuMode,
  ) => ReadonlyArray<ContextMenuItem>;
}

/**
 * cmd-pan r6 P2-1 — the SINGLE menu-level choke point. Recursively wrap every
 * item's `onSelect` so that invoking ANY item (current OR future) first tears
 * down a live realtime-PAN session — EXCEPT the 'Pan' item, which STARTS one and
 * must not cancel itself. This covers the direct-`dispatch` items
 * (`deselect-all`, `properties`) that bypass `submitCommandText` /
 * `withPanSupersede`, plus every submenu child (clipboard cut/copy/paste …), by
 * construction. Adding a new menu item needs no extra wiring — it is wrapped
 * here automatically, ending the per-item convergence spiral.
 *
 * Items with `children` carry no `onSelect` of their own (the row ignores it),
 * so a parent row is left untouched; only its leaf children are wrapped.
 */
function wrapItemActionsWithPanSupersede(
  items: ReadonlyArray<ContextMenuItem>,
  supersedePanSession: () => void,
): ReadonlyArray<ContextMenuItem> {
  return items.map((item) => {
    const wrappedChildren = item.children
      ? wrapItemActionsWithPanSupersede(item.children, supersedePanSession)
      : undefined;
    // The 'Pan' item is the sole exclusion — it starts a session, so it must NOT
    // supersede (which would immediately cancel the session it just began).
    const onSelect =
      item.id === PAN_ITEM_ID || !item.onSelect
        ? item.onSelect
        : () => {
            supersedePanSession();
            item.onSelect!();
          };
    if (onSelect === item.onSelect && wrappedChildren === item.children) {
      return item;
    }
    return { ...item, onSelect, children: wrappedChildren };
  });
}

export function useStageContextMenu({
  state,
  draftSession,
  t,
  submitCommandTextRef,
  dispatch,
  supersedePanSession,
  onOpenOsnapSettings,
}: UseStageContextMenuParams): UseStageContextMenuResult {
  const [stageContextMenu, setStageContextMenu] =
    useState<StageContextMenuState | null>(null);

  const closeStageContextMenu = useCallback(() => {
    setStageContextMenu(null);
  }, []);

  const handleStageContextMenu = useCallback(
    (screen: { x: number; y: number }, shiftKey: boolean): void => {
      // W2.5 OSNAP-OVERRIDE-MODES-S2 — AutoCAD's Shift+right-click Object
      // Snap cursor menu, available during an active POINT-PICK prompt
      // (including the FIRST point of a session, hence checking the step's
      // registry-declared `acceptsPointInput` rather than an anchored-only
      // check). Local Codex review (round-2) P2 — `draftSession != null`
      // alone is WRONG: `DraftStep` also covers non-point prompts such as
      // `move-select` (entity SELECTION), `trim-select-edges`, and
      // `text-content` (text entry) — opening the override menu there and
      // arming `extint`/`extappint` would swallow the NEXT canvas click as an
      // operand pick instead of the selection/text input the command
      // actually needs, stalling it. `getDraftStepDescriptor(...).
      // acceptsPointInput` is the SAME single-source-of-truth field the draft
      // registry already uses to distinguish a coordinate-accepting step from
      // a scalar/mode/selection/text one (draftStepRegistry.ts) — reusing it
      // here avoids re-deriving a second, driftable classification.
      // This is a DISTINCT menu from SHORTCUTMENU; skip the bitmask
      // mode-selection below entirely for the shift case.
      if (
        shiftKey &&
        draftSession != null &&
        getDraftStepDescriptor(draftSession.step).acceptsPointInput
      ) {
        setStageContextMenu({ screenPos: screen, mode: "osnap" });
        return;
      }
      let mode: ContextMenuMode;
      let bitMask: number;
      if (draftSession) {
        mode = "command";
        bitMask = 8;
      } else if (state.selection.entityIds.length > 0) {
        mode = "edit";
        bitMask = 2;
      } else {
        mode = "default";
        bitMask = 1;
      }
      if ((SHORTCUTMENU_BITS & bitMask) === 0) {
        // Bit not set → legacy RIGHTCLICKENTER fallback (Enter-on-RMB).
        submitCommandTextRef.current?.("");
        return;
      }
      setStageContextMenu({ screenPos: screen, mode });
    },
    [draftSession, state.selection.entityIds.length, submitCommandTextRef],
  );

  const buildContextMenuItems = useCallback(
    (mode: ContextMenuMode): ReadonlyArray<ContextMenuItem> => {
      // cmd-pan r6 P2-1 — build the RAW per-mode items, then funnel the WHOLE
      // list through the single pan-supersede choke point below. Every item's
      // action (except 'Pan') is wrapped to tear down a live realtime-PAN first,
      // so direct-`dispatch` items (`deselect-all`, `properties`) no longer leak
      // a stuck pan when chosen mid-pan — by construction, not per-item.
      const buildRaw = (): ReadonlyArray<ContextMenuItem> => {
        const lastSubmitted = state.commandLine.lastSubmitted;
        const dispatchSubmit = (text: string): void => {
          submitCommandTextRef.current?.(text);
        };
        const clipboardChildren: ContextMenuItem[] = [
          {
            id: "clip-cut",
            label: t("context_menu.clipboard.cut"),
            shortcut: "Ctrl+X",
            onSelect: () => dispatchSubmit("CUTCLIP"),
          },
          {
            id: "clip-copy",
            label: t("context_menu.clipboard.copy"),
            shortcut: "Ctrl+C",
            onSelect: () => dispatchSubmit("COPYCLIP"),
          },
          {
            id: "clip-copy-base",
            label: t("context_menu.clipboard.copy_with_base"),
            shortcut: "Ctrl+Shift+C",
            onSelect: () => dispatchSubmit("COPYBASE"),
          },
          {
            id: "clip-paste",
            label: t("context_menu.clipboard.paste"),
            shortcut: "Ctrl+V",
            onSelect: () => dispatchSubmit("PASTECLIP"),
          },
          {
            id: "clip-paste-as-block",
            label: t("context_menu.clipboard.paste_as_block"),
            shortcut: "Ctrl+Shift+V",
            onSelect: () => dispatchSubmit("PASTEBLOCK"),
          },
          {
            id: "clip-paste-orig",
            label: t("context_menu.clipboard.paste_to_original"),
            onSelect: () => dispatchSubmit("PASTEORIG"),
          },
        ];

        if (mode === "command") {
          // Command mode (bit 8) — Enter / Cancel / recent input. Real
          // command-option chips would require introspection into
          // draftSession.options; phase-2 scope.
          return [
            {
              id: "cmd-enter",
              label: t("context_menu.command.enter"),
              shortcut: "Enter",
              onSelect: () => dispatchSubmit(""),
            },
            {
              id: "cmd-cancel",
              label: t("context_menu.command.cancel"),
              shortcut: "Esc",
              onSelect: () => dispatchSubmit("\x1b"),
            },
            {
              id: "cmd-recent",
              label: t("context_menu.command.recent"),
              disabled: !lastSubmitted,
              onSelect: () => {
                if (lastSubmitted) dispatchSubmit(lastSubmitted);
              },
            },
          ];
        }

        if (mode === "osnap") {
          // W2.5 OSNAP-OVERRIDE-MODES-S2 — AutoCAD's Object Snap cursor
          // menu, Shift+right-click during an active point-pick. Scoped
          // TIGHTLY to what this slice's resolver actually supports: the two
          // DEFERRED override-only modes (Extended Intersection / Extended
          // Apparent Intersection) this slice wires up, plus a settings
          // shortcut. The 13 running-snap modes already have Shift+letter
          // HELD-key coverage from OSNAP-TEMPOVERRIDE-WIRE and are
          // deliberately NOT reproduced here as menu items — rebuilding
          // AutoCAD's full ~20-item Object Snap menu is out of scope.
          return [
            {
              id: "osnap-extint",
              label: t("dsettings.osnap.extint"),
              onSelect: () =>
                useSysvarStore
                  .getState()
                  .setOsnapExtendedOverride({ mode: "extint", firstOwnerId: null }),
            },
            {
              id: "osnap-extappint",
              label: t("dsettings.osnap.extappint"),
              onSelect: () =>
                useSysvarStore
                  .getState()
                  .setOsnapExtendedOverride({ mode: "extappint", firstOwnerId: null }),
            },
            {
              id: "osnap-settings",
              label: t("context_menu.osnap.settings"),
              divider: true,
              disabled: !onOpenOsnapSettings,
              onSelect: () => onOpenOsnapSettings?.(),
            },
          ];
        }

        if (mode === "edit") {
          // Edit mode (bit 2) — operations on the current selection.
          const hasSelection = state.selection.entityIds.length > 0;
          return [
            {
              id: "repeat-last",
              label: t("context_menu.repeat_last"),
              disabled: !lastSubmitted,
              onSelect: () => {
                if (lastSubmitted) dispatchSubmit(lastSubmitted);
              },
            },
            {
              id: "recent-input",
              label: t("context_menu.recent_input"),
              disabled: true,
            },
            {
              id: "clipboard",
              label: t("context_menu.clipboard"),
              children: clipboardChildren,
            },
            {
              id: "isolate",
              label: t("context_menu.isolate"),
              disabled: true,
            },
            {
              id: "erase",
              label: t("context_menu.erase"),
              shortcut: "Del",
              disabled: !hasSelection,
              divider: true,
              onSelect: () => dispatchSubmit("ERASE"),
            },
            {
              id: "move",
              label: t("context_menu.move"),
              disabled: !hasSelection,
              onSelect: () => dispatchSubmit("MOVE"),
            },
            {
              id: "copy",
              label: t("context_menu.copy"),
              disabled: !hasSelection,
              onSelect: () => dispatchSubmit("COPY"),
            },
            {
              id: "scale",
              label: t("context_menu.scale"),
              disabled: !hasSelection,
              onSelect: () => dispatchSubmit("SCALE"),
            },
            {
              id: "rotate",
              label: t("context_menu.rotate"),
              disabled: !hasSelection,
              onSelect: () => dispatchSubmit("ROTATE"),
            },
            {
              id: "draw-order",
              label: t("context_menu.draw_order"),
              disabled: true,
              divider: true,
            },
            {
              id: "group",
              label: t("context_menu.group"),
              disabled: true,
            },
            {
              id: "add-selected",
              label: t("context_menu.add_selected"),
              disabled: true,
            },
            {
              id: "select-similar",
              label: t("context_menu.select_similar"),
              disabled: true,
            },
            {
              id: "deselect-all",
              label: t("context_menu.deselect_all"),
              divider: true,
              onSelect: () =>
                dispatch({ type: "shell/clear-selection", source: "scene" }),
            },
            {
              id: "subobject-filter",
              label: t("context_menu.subobject_filter"),
              disabled: true,
            },
            {
              id: "quick-select",
              label: t("context_menu.quick_select"),
              onSelect: () => dispatchSubmit("QSELECT"),
            },
            {
              id: "properties",
              label: t("context_menu.properties"),
              shortcut: "Ctrl+1",
              onSelect: () =>
                dispatch({
                  type: "shell/open-panel",
                  source: "scene",
                  panel: "properties",
                }),
            },
            {
              id: "quick-properties",
              label: t("context_menu.quick_properties"),
              disabled: true,
            },
          ];
        }

        // ── Default mode (bit 1) — empty stage, no selection, no command ──
        return [
          {
            id: "repeat-last",
            label: t("context_menu.repeat_last"),
            disabled: !lastSubmitted,
            onSelect: () => {
              if (lastSubmitted) dispatchSubmit(lastSubmitted);
            },
          },
          {
            id: "recent-input",
            label: t("context_menu.recent_input"),
            disabled: true,
          },
          {
            id: "clipboard",
            label: t("context_menu.clipboard"),
            children: clipboardChildren,
          },
          {
            id: "undo",
            label: t("context_menu.undo"),
            shortcut: "Ctrl+Z",
            divider: true,
            onSelect: () => dispatchSubmit("U"),
          },
          {
            id: "redo",
            label: t("context_menu.redo"),
            shortcut: "Ctrl+Y",
            onSelect: () => dispatchSubmit("REDO"),
          },
          {
            id: "pan",
            label: t("context_menu.pan"),
            divider: true,
            onSelect: () => dispatchSubmit("PAN"),
          },
          {
            id: "zoom",
            label: t("context_menu.zoom"),
            onSelect: () => dispatchSubmit("ZOOM"),
          },
          {
            id: "steering-wheels",
            label: t("context_menu.steeringwheels"),
            disabled: true,
          },
          {
            id: "action-recorder",
            label: t("context_menu.action_recorder"),
            disabled: true,
          },
          {
            id: "subobject-filter",
            label: t("context_menu.subobject_filter"),
            disabled: true,
            divider: true,
          },
          {
            id: "quick-select",
            label: t("context_menu.quick_select"),
            onSelect: () => dispatchSubmit("QSELECT"),
          },
          {
            id: "quickcalc",
            label: t("context_menu.quickcalc"),
            disabled: true,
          },
          {
            id: "find",
            label: t("context_menu.find"),
            disabled: true,
          },
          {
            id: "options",
            label: t("context_menu.options"),
            divider: true,
            onSelect: () => dispatchSubmit("OPTIONS"),
          },
        ];
      };
      // ── The SINGLE menu-level pan-supersede choke point (cmd-pan r6 P2-1) ──
      return wrapItemActionsWithPanSupersede(buildRaw(), supersedePanSession);
    },
    [
      state.commandLine.lastSubmitted,
      state.selection.entityIds.length,
      submitCommandTextRef,
      dispatch,
      t,
      supersedePanSession,
      onOpenOsnapSettings,
    ],
  );

  return {
    stageContextMenu,
    handleStageContextMenu,
    closeStageContextMenu,
    buildContextMenuItems,
  };
}
